import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import chinaGeo from '../../../assets/geo/china.json';

// 只需要注册一次
echarts.registerMap('china', chinaGeo);

// 城市数据:经纬度(GCJ-02 坐标系,DataV 的地图能直接对齐)
const cities = [
  { name: '北京', coord: [116.405, 39.904] },
  { name: '上海', coord: [121.473, 31.230] },
  { name: '广州', coord: [113.264, 23.129] },
  { name: '深圳', coord: [114.057, 22.543] },
  { name: '珠海', coord: [113.576, 22.271] },
];

// 服务连线:从广州发散辐射到北上深珠
const links = [
  { from: '广州', to: '北京' },
  { from: '广州', to: '上海' },
  { from: '广州', to: '深圳' },
  { from: '广州', to: '珠海' },
];

// 城市名 → 坐标 的快速查找
const coordMap = Object.fromEntries(cities.map((c) => [c.name, c.coord]));

const ZOOM_MIN = 0.8;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.25;
const ZOOM_INIT = 1.1;

const ServiceNetwork = () => {
  const chartRef = useRef(null);
  const echartsInstanceRef = useRef(null);
  const sliderRef = useRef(null);
  const draggingRef = useRef(false);

  const [zoom, setZoom] = useState(ZOOM_INIT);

  // 完整的 option 配置,geo.zoom 跟随 state 变化
  const option = useMemo(
    () => ({
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#d6e4f5',
        borderWidth: 1,
        textStyle: { color: '#1a3a5c', fontSize: 13 },
        padding: [6, 12],
        formatter: (params) => {
          if (params.seriesType === 'effectScatter') {
            return `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#1890ff;margin-right:6px;vertical-align:middle"></span>${params.name}`;
          }
          return params.name;
        },
      },
      geo: {
        map: 'china',
        roam: 'move', // 只允许拖动平移,缩放交给自己的 + / - / 滑块
        zoom,
        scaleLimit: { min: ZOOM_MIN, max: ZOOM_MAX },
        layoutCenter: ['50%', '54%'],
        layoutSize: '95%',
        label: { show: false },
        itemStyle: {
          areaColor: '#dceaf7',
          borderColor: '#5b8bd0',
          borderWidth: 1,
          shadowColor: 'rgba(91, 139, 208, 0.18)',
          shadowBlur: 12,
          shadowOffsetY: 4,
        },
        emphasis: { disabled: true },
        select: { disabled: true },
      },
      series: [
        // 服务连线(带弧度的流光线)
        {
          name: '服务连线',
          type: 'lines',
          coordinateSystem: 'geo',
          zlevel: 2,
          effect: {
            show: true,
            period: 5,
            trailLength: 0.4,
            symbol: 'arrow',
            symbolSize: 6,
            color: '#1890ff',
          },
          lineStyle: {
            color: '#1890ff',
            width: 1.5,
            opacity: 0.8,
            curveness: 0.35,
          },
          data: links.map((l) => ({
            fromName: l.from,
            toName: l.to,
            coords: [coordMap[l.from], coordMap[l.to]],
          })),
        },
        // 城市点(带涟漪特效)
        {
          name: '服务点',
          type: 'effectScatter',
          coordinateSystem: 'geo',
          zlevel: 3,
          rippleEffect: {
            brushType: 'stroke',
            scale: 3.5,
            period: 3.5,
          },
          symbol: 'circle',
          symbolSize: 10,
          itemStyle: {
            color: '#1890ff',
            shadowBlur: 8,
            shadowColor: 'rgba(24, 144, 255, 0.6)',
          },
          label: {
            show: true,
            position: 'right',
            distance: 6,
            color: '#1a3a5c',
            fontSize: 13,
            fontWeight: 600,
            formatter: '{b}',
          },
          data: cities.map((c) => ({ name: c.name, value: c.coord })),
        },
      ],
    }),
    [zoom]
  );

  // 同步 zoom 到 ECharts 实例
  const syncZoomToChart = useCallback((next) => {
    const instance =
      echartsInstanceRef.current || chartRef.current?.getEchartsInstance();
    if (instance) {
      instance.setOption({ geo: { zoom: next } });
    }
  }, []);

  // 通用缩放入口:state + setOption 双保险
  const applyZoom = useCallback(
    (next) => {
      const clamped = Math.min(
        ZOOM_MAX,
        Math.max(ZOOM_MIN, +next.toFixed(2))
      );
      if (clamped === zoom) return;
      setZoom(clamped);
      syncZoomToChart(clamped);
    },
    [zoom, syncZoomToChart]
  );

  const handleZoomStep = (delta) => applyZoom(zoom + delta);

  // —— 滑块拖动:把鼠标/触摸位置映射回 zoom ——
  // 顶部 = ZOOM_MAX(放大),底部 = ZOOM_MIN(缩小)
  const updateZoomFromPointer = useCallback(
    (clientY) => {
      if (!sliderRef.current) return;
      const rect = sliderRef.current.getBoundingClientRect();
      // 0 = 顶部,1 = 底部
      const ratio = Math.max(
        0,
        Math.min(1, (clientY - rect.top) / rect.height)
      );
      // 顶部对应 ZOOM_MAX,底部对应 ZOOM_MIN
      const next = ZOOM_MAX - ratio * (ZOOM_MAX - ZOOM_MIN);
      applyZoom(next);
    },
    [applyZoom]
  );

  // 滑块按下:同时支持鼠标和触摸
  const handleSliderPointerDown = (e) => {
    draggingRef.current = true;
    // 鼠标:抓取事件
    if (e.pointerId !== undefined && e.currentTarget.setPointerCapture) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    const clientY = e.clientY;
    if (clientY != null) updateZoomFromPointer(clientY);
    e.preventDefault();
  };

  // 全局监听 move / up,这样拖出滑块区域也不会断
  // 用 ref 持有最新回调,避免 zoom 变化导致监听器被反复拆卸
  const updateZoomFromPointerRef = useRef(updateZoomFromPointer);
  updateZoomFromPointerRef.current = updateZoomFromPointer;

  useEffect(() => {
    const handleMove = (e) => {
      if (!draggingRef.current) return;
      const clientY = e.clientY;
      if (clientY != null) updateZoomFromPointerRef.current(clientY);
    };
    const handleUp = () => {
      draggingRef.current = false;
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, []);

  // onChartReady 捕获 ECharts 实例
  const handleChartReady = (instance) => {
    echartsInstanceRef.current = instance;
  };

  // 卸载时清理
  useEffect(() => {
    return () => {
      echartsInstanceRef.current = null;
    };
  }, []);

  // 手柄 top 百分比:顶部 = ZOOM_MAX,底部 = ZOOM_MIN
  const handleTopPercent =
    ((ZOOM_MAX - zoom) / (ZOOM_MAX - ZOOM_MIN)) * 100;

  return (
    <section className="service-network">
      <div className="service-network-inner">
        <header className="service-network-head">
          <h2 className="service-network-title">服务网络</h2>
          <span className="service-network-underline" />
          <p className="service-network-subtitle">
            覆盖全国核心城市,本地化服务随时响应
          </p>
        </header>

        <div className="service-network-map-wrap">
          <ReactECharts
            ref={chartRef}
            option={option}
            onChartReady={handleChartReady}
            style={{ width: '100%', height: '100%' }}
            opts={{ renderer: 'canvas' }}
            notMerge={false}
            lazyUpdate
          />

          {/* 缩放控件:上 + 下 -,中间可拖动滑块 */}
          <div className="service-network-zoom">
            <button
              type="button"
              className="service-network-zoom-btn"
              onClick={() => handleZoomStep(ZOOM_STEP)}
              disabled={zoom >= ZOOM_MAX}
              aria-label="放大地图"
            >
              <PlusOutlined />
            </button>

            {/* 滑块区域:含轨道 + 圆形手柄 */}
            <div
              className="service-network-zoom-slider"
              ref={sliderRef}
              onMouseDown={handleSliderPointerDown}
              role="slider"
              aria-valuemin={ZOOM_MIN}
              aria-valuemax={ZOOM_MAX}
              aria-valuenow={zoom}
              tabIndex={0}
              style={{ '--zoom-fill': `${handleTopPercent}%` }}
            >
              <span className="service-network-zoom-track" />
              <span
                className="service-network-zoom-handle"
                style={{ top: `${handleTopPercent}%` }}
              />
            </div>

            <button
              type="button"
              className="service-network-zoom-btn"
              onClick={() => handleZoomStep(-ZOOM_STEP)}
              disabled={zoom <= ZOOM_MIN}
              aria-label="缩小地图"
            >
              <MinusOutlined />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceNetwork;
